import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Bot, 
  Wand2, 
  AlertCircle,
  CheckCircle,
  Copy,
  Download,
  Loader2,
  Database,
  Edit3,
  Plus,
  X
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIModuleCreator = ({ open, onOpenChange, entities }) => {
  // Mode selection
  const [scenario, setScenario] = useState("scenario2"); // "scenario2" or "scenario3"
  
  // Common fields
  const [sourceSystem, setSourceSystem] = useState("");
  const [targetSystem, setTargetSystem] = useState("");
  const [referencePair, setReferencePair] = useState("");
  const [moduleName, setModuleName] = useState("");
  const [moduleDescription, setModuleDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedConfig, setGeneratedConfig] = useState(null);
  const [error, setError] = useState(null);
  const [sqlLoading, setSqlLoading] = useState(false);
  const [generatedSqlScripts, setGeneratedSqlScripts] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableConfig, setEditableConfig] = useState(null);
  const [editingDependentIndex, setEditingDependentIndex] = useState(null);
  const [editingMappingIndex, setEditingMappingIndex] = useState(null);
  const [referencePairs, setReferencePairs] = useState([]);
  const [availableDependents, setAvailableDependents] = useState([]);
  const [selectedDependents, setSelectedDependents] = useState([]);
  const [customDependents, setCustomDependents] = useState([]);
  const [showAddDependentModal, setShowAddDependentModal] = useState(false);
  const [newDependentName, setNewDependentName] = useState("");
  const [jsonEditorModal, setJsonEditorModal] = useState({ open: false, title: "", value: null, path: null, table: null, field: null });
  const [jsonEditorView, setJsonEditorView] = useState("tree");
  const [tempJsonValue, setTempJsonValue] = useState("");
  
  // New Target specific fields
  const [customDependentTargets, setCustomDependentTargets] = useState([]);
  const [customMappingTargets, setCustomMappingTargets] = useState([]);
  const [showAddDependentTargetModal, setShowAddDependentTargetModal] = useState(false);
  const [showAddMappingTargetModal, setShowAddMappingTargetModal] = useState(false);
  const [newTargetData, setNewTargetData] = useState({
    name: "",
    sourceKeyForChild: "",
    targetUniqueIdentifier: "",
    type: "dependent" // "dependent" or "mapping"
  });
  
  // Existing targets preview
  const [existingMappingTargetsPreview, setExistingMappingTargetsPreview] = useState([]);
  const [loadingMappingTargetsPreview, setLoadingMappingTargetsPreview] = useState(false);

  // Scenario 3 specific fields
  const [newSourceName, setNewSourceName] = useState("");
  const [newTargetName, setNewTargetName] = useState("");
  const [createNewSource, setCreateNewSource] = useState(false);
  const [createNewTarget, setCreateNewTarget] = useState(false);
  
  // validate_auth fields with default JSON structure
  const defaultApiModel = {
    "apiUrl": "https://dev.api.saasgenie.ai/system/entity",
    "headers": [
      {
        "key": "Content-Type",
        "req": false,
        "value": "application/json",
        "description": ""
      }
    ],
    "queryParams": [
      {
        "key": "domainUrl",
        "req": true,
        "value": "",
        "description": ""
      },
      {
        "key": "username",
        "req": true,
        "value": "",
        "description": ""
      },
      {
        "key": "password",
        "req": true,
        "value": "",
        "description": ""
      }
    ],
    "name": "get_system_entity",
    "method_type": "GET"
  };

  const [sourceAuth, setSourceAuth] = useState({
    vendorId: "",
    apimodel: defaultApiModel,
    authType: "basic"
  });
  const [targetAuth, setTargetAuth] = useState({
    vendorId: "",
    apimodel: defaultApiModel,
    authType: "basic"
  });
  
  // source_filter_options fields
  const [sourceFilterOptions, setSourceFilterOptions] = useState([
    { name: "page", value: "1" },
    { name: "per_page", value: "100" }
  ]);

  // Fetch reference pairs on mount
  React.useEffect(() => {
    const fetchReferencePairs = async () => {
      try {
        const response = await axios.get(`${API}/reference-pairs`);
        setReferencePairs(response.data);
      } catch (err) {
        console.error("Error fetching reference pairs:", err);
        toast.error("Failed to load reference pairs");
      }
    };
    
    if (open) {
      fetchReferencePairs();
    }
  }, [open]);

  // Fetch dependent targets when reference pair changes
  React.useEffect(() => {
    if (referencePair) {
      const selectedPair = referencePairs.find(p => p.id.toString() === referencePair);
      if (selectedPair) {
        // Fetch dependent targets from the backend
        fetchDependentTargets(selectedPair.id);
      }
    }
  }, [referencePair, referencePairs]);

  const fetchDependentTargets = async (pairId) => {
    try {
      const response = await axios.get(`${API}/reference-pair-dependents/${pairId}`);
      setAvailableDependents(response.data);
      // By default, select all available dependents
      setSelectedDependents(response.data.map(d => d.name));
    } catch (err) {
      console.error("Error fetching dependent targets:", err);
      setAvailableDependents([]);
      setSelectedDependents([]);
    }
  };

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setScenario("scenario2");
      setSourceSystem("");
      setTargetSystem("");
      setReferencePair("");
      setModuleName("");
      setModuleDescription("");
      setGeneratedConfig(null);
      setError(null);
      setLoading(false);
      setSqlLoading(false);
      setGeneratedSqlScripts(null);
      setIsEditing(false);
      setEditableConfig(null);
      setAvailableDependents([]);
      setSelectedDependents([]);
      setCustomDependents([]);
      setShowAddDependentModal(false);
      setNewDependentName("");
      // Scenario 3 fields
      setNewSourceName("");
      setNewTargetName("");
      setCreateNewSource(false);
      setCreateNewTarget(false);
      setSourceAuth({ vendorId: "", apimodel: defaultApiModel, authType: "basic" });
      setTargetAuth({ vendorId: "", apimodel: defaultApiModel, authType: "basic" });
      setSourceFilterOptions([
        { name: "page", value: "1" },
        { name: "per_page", value: "100" }
      ]);
    }
  }, [open]);

  // Get unique source and target systems from existing entities
  const getUniqueSources = () => {
    return [...new Set(entities.map(e => e.source))].sort();
  };

  const getUniqueTargets = () => {
    return [...new Set(entities.map(e => e.target))].sort();
  };

  const handleAddCustomDependent = () => {
    if (!newDependentName.trim()) {
      toast.error("Please enter a dependent target name");
      return;
    }
    
    if (customDependents.includes(newDependentName) || selectedDependents.includes(newDependentName)) {
      toast.error("This dependent target already exists");
      return;
    }

    setCustomDependents([...customDependents, newDependentName]);
    setNewDependentName("");
    setShowAddDependentModal(false);
    toast.success(`Added custom dependent: ${newDependentName}`);
  };

  const handleToggleDependent = (dependentName) => {
    if (selectedDependents.includes(dependentName)) {
      setSelectedDependents(selectedDependents.filter(d => d !== dependentName));
    } else {
      setSelectedDependents([...selectedDependents, dependentName]);
    }
  };

  const handleRemoveCustomDependent = (dependentName) => {
    setCustomDependents(customDependents.filter(d => d !== dependentName));
  };

  // Scenario 3 helper functions
  const addSourceFilterOption = () => {
    setSourceFilterOptions([...sourceFilterOptions, { name: "", value: "" }]);
  };

  const removeSourceFilterOption = (index) => {
    setSourceFilterOptions(sourceFilterOptions.filter((_, i) => i !== index));
  };

  const updateSourceFilterOption = (index, field, value) => {
    const updated = [...sourceFilterOptions];
    updated[index][field] = value;
    setSourceFilterOptions(updated);
  };

  // Helper to display JSON without escape characters
  const displayJson = (value, defaultValue = {}) => {
    if (!value) return JSON.stringify(defaultValue, null, 2);
    
    // If it's already a string, try to parse it
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed, null, 2);
      } catch {
        // If parse fails, it might already be formatted
        return value;
      }
    }
    
    // If it's an object/array, stringify it
    return JSON.stringify(value, null, 2);
  };

  // Open JSON editor modal
  const openJsonEditor = (title, value, table, field, dependentIndex = null) => {
    let jsonValue;
    try {
      // Parse and format JSON properly
      if (typeof value === 'string') {
        const parsed = JSON.parse(value);
        jsonValue = JSON.stringify(parsed, null, 2);
      } else {
        jsonValue = JSON.stringify(value || {}, null, 2);
      }
    } catch {
      jsonValue = typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2);
    }
    
    setTempJsonValue(jsonValue);
    setJsonEditorModal({
      open: true,
      title,
      value: jsonValue,
      table,
      field,
      dependentIndex
    });
    setJsonEditorView("code"); // Start with code view for better initial experience
  };

  // Save JSON from editor
  const saveJsonEdit = () => {
    try {
      const parsed = JSON.parse(tempJsonValue);
      const { table, field, dependentIndex } = jsonEditorModal;
      
      if (table === 'source_auth') {
        // Editing source auth field
        setSourceAuth({...sourceAuth, [field]: parsed});
      } else if (table === 'target_auth') {
        // Editing target auth field
        setTargetAuth({...targetAuth, [field]: parsed});
      } else if (dependentIndex !== null) {
        // Editing dependent target
        const newConfig = { ...generatedConfig };
        newConfig.generated_config.migration_targets.dependentTargets[dependentIndex][field] = parsed;
        setGeneratedConfig(newConfig);
      } else if (table === 'migration_targets.mainTarget' || table === 'migration_targets') {
        // Editing main target field
        const newConfig = { ...generatedConfig };
        newConfig.generated_config.migration_targets.mainTarget[field] = parsed;
        setGeneratedConfig(newConfig);
      } else {
        // Editing other table field (migration_sources, etc.)
        const newConfig = { ...generatedConfig };
        newConfig.generated_config[table][field] = parsed;
        setGeneratedConfig(newConfig);
      }
      
      setJsonEditorModal({ open: false, title: "", value: null, table: null, field: null });
      toast.success("JSON updated successfully");
    } catch (err) {
      console.error("JSON parse error:", err);
      toast.error("Invalid JSON format: " + err.message);
    }
  };

  // Handle JSON text change
  const handleJsonTextChange = (newValue) => {
    setTempJsonValue(newValue);
  };

  // Format JSON for code view
  const formatJson = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  // Tree node component for JSON tree view
  const TreeNode = ({ keyName, value, path, onUpdate, onDelete }) => {
    const [isExpanded, setIsExpanded] = React.useState(true);
    const [isEditing, setIsEditing] = React.useState(false);
    const [editValue, setEditValue] = React.useState(value);

    const isObject = typeof value === 'object' && value !== null && !Array.isArray(value);
    const isArray = Array.isArray(value);
    const isPrimitive = !isObject && !isArray;

    const handleUpdate = () => {
      let finalValue = editValue;
      if (editValue === 'true') finalValue = true;
      else if (editValue === 'false') finalValue = false;
      else if (editValue === 'null') finalValue = null;
      else if (!isNaN(editValue) && editValue !== '') finalValue = Number(editValue);
      
      onUpdate(path, finalValue);
      setIsEditing(false);
    };

    const handleAddProperty = () => {
      const newKey = prompt('Enter property name:');
      if (newKey) {
        onUpdate([...path, newKey], '');
      }
    };

    const handleAddArrayItem = () => {
      const currentArray = Array.isArray(value) ? value : [];
      onUpdate(path, [...currentArray, '']);
    };

    return (
      <div className="ml-4 my-1">
        <div className="flex items-center gap-2 group">
          {(isObject || isArray) && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          
          <span className="font-medium text-blue-600">{keyName}:</span>
          
          {isPrimitive && (
            <>
              {isEditing ? (
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={handleUpdate}
                  onKeyPress={(e) => e.key === 'Enter' && handleUpdate()}
                  className="flex-1 px-2 py-1 border rounded text-sm"
                  autoFocus
                />
              ) : (
                <span
                  onClick={() => setIsEditing(true)}
                  className="flex-1 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                >
                  {String(value)}
                </span>
              )}
            </>
          )}
          
          {isArray && <span className="text-gray-500">[{value.length} items]</span>}
          {isObject && <span className="text-gray-500">{`{${Object.keys(value).length} props}`}</span>}
          
          <button
            onClick={() => onDelete(path)}
            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isExpanded && (isObject || isArray) && (
          <div className="ml-4 border-l border-gray-300 pl-2">
            {isObject && Object.entries(value).map(([key, val]) => (
              <TreeNode
                key={key}
                keyName={key}
                value={val}
                path={[...path, key]}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
            
            {isArray && value.map((item, idx) => (
              <TreeNode
                key={idx}
                keyName={idx}
                value={item}
                path={[...path, idx]}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
            
            <button
              onClick={isArray ? handleAddArrayItem : handleAddProperty}
              className="text-blue-600 text-sm mt-1 hover:underline flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Add {isArray ? 'Item' : 'Property'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Update tree data
  const updateTreeData = (path, value) => {
    try {
      const data = JSON.parse(tempJsonValue);
      let current = data;
      
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      
      current[path[path.length - 1]] = value;
      setTempJsonValue(JSON.stringify(data, null, 2));
    } catch (err) {
      console.error('Error updating tree:', err);
    }
  };

  // Delete tree node
  const deleteTreeNode = (path) => {
    if (path.length === 0) return;
    
    try {
      const data = JSON.parse(tempJsonValue);
      let current = data;
      
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      
      if (Array.isArray(current)) {
        current.splice(path[path.length - 1], 1);
      } else {
        delete current[path[path.length - 1]];
      }
      
      setTempJsonValue(JSON.stringify(data, null, 2));
    } catch (err) {
      console.error('Error deleting node:', err);
    }
  };

  // Fetch existing mapping targets for preview
  const fetchExistingMappingTargets = async (referenceId) => {
    if (!referenceId || referenceId === 'none' || createNewTarget) {
      setExistingMappingTargetsPreview([]);
      return;
    }
    
    setLoadingMappingTargetsPreview(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/source-target-pair-details/${sourceSystem}/${targetSystem}/entities?reference_pair_id=${referenceId}`);
      if (response.ok) {
        const data = await response.json();
        // Look for mapping targets in the response
        if (data.entities && data.entities.length > 0) {
          const entity = data.entities[0];
          if (entity.mappingTargets && entity.mappingTargets.length > 0) {
            setExistingMappingTargetsPreview(entity.mappingTargets.map(target => ({
              name: target.name || 'Unnamed',
              type: target.target_type || 'API',
              id: target.id
            })));
          } else {
            setExistingMappingTargetsPreview([]);
          }
        } else {
          setExistingMappingTargetsPreview([]);
        }
      }
    } catch (error) {
      console.error('Error fetching existing mapping targets:', error);
      setExistingMappingTargetsPreview([]);
    } finally {
      setLoadingMappingTargetsPreview(false);
    }
  };

  // Update existing mapping targets preview when reference pair changes
  React.useEffect(() => {
    if (referencePair && !createNewTarget && targetSystem) {
      fetchExistingMappingTargets(referencePair);
    } else {
      setExistingMappingTargetsPreview([]);
    }
  }, [referencePair, createNewTarget, targetSystem]);

  const handleGenerateModule = async () => {
    // Validation based on scenario
    if (scenario === "scenario2") {
      if (!sourceSystem || !targetSystem || !moduleName || !referencePair) {
        toast.error("Please fill in all required fields including reference pair");
        return;
      }
    } else {
      // Scenario 3 validation
      const effectiveSourceSystem = createNewSource ? newSourceName : sourceSystem;
      const effectiveTargetSystem = createNewTarget ? newTargetName : targetSystem;
      
      if (!effectiveSourceSystem || !effectiveTargetSystem || !moduleName) {
        toast.error("Please fill in source system, target system, and module name");
        return;
      }
      
      if (createNewSource && (!sourceAuth.vendorId || !sourceAuth.apimodel)) {
        toast.error("Please fill in all source authentication details (Vendor ID and API Model required)");
        return;
      }
      
      if (createNewTarget && (!targetAuth.vendorId || !targetAuth.apimodel)) {
        toast.error("Please fill in all target authentication details (Vendor ID and API Model required)");
        return;
      }
    }

    setLoading(true);
    setError(null);
    setGeneratedConfig(null);

    try {
      const endpoint = scenario === "scenario2" ? "/ai-create-module" : "/ai-create-module-scenario3";
      let requestPayload;
      
      if (scenario === "scenario2") {
        requestPayload = {
          source_system: sourceSystem,
          target_system: targetSystem,
          reference_pair_id: referencePair,
          module_name: moduleName,
          module_description: moduleDescription,
          selected_dependents: selectedDependents,
          custom_dependents: customDependents
        };
      } else {
        requestPayload = {
          source_system: createNewSource ? newSourceName : sourceSystem,
          target_system: createNewTarget ? newTargetName : targetSystem,
          module_name: moduleName,
          module_description: moduleDescription,
          create_new_source: createNewSource,
          create_new_target: createNewTarget,
          reference_pair_id: referencePair ? parseInt(referencePair) : null,  // Send reference_pair_id for all mixed mode scenarios
          source_auth: createNewSource ? sourceAuth : null,
          target_auth: createNewTarget ? targetAuth : null,
          source_filter_options: createNewSource ? sourceFilterOptions.filter(opt => opt.name && opt.value) : null,
          custom_dependent_targets: createNewTarget ? customDependentTargets : null,
          custom_mapping_targets: createNewTarget ? customMappingTargets : null
        };
      }

      const response = await axios.post(`${API}${endpoint}`, requestPayload);

      if (response.data.success) {
        setGeneratedConfig(response.data);
        setEditableConfig(JSON.parse(JSON.stringify(response.data.generated_config))); // Deep copy
        toast.success("AI-powered module configuration generated successfully!");
      } else {
        const errorMsg = response.data.error || "Failed to generate module configuration";
        setError(errorMsg);
        
        if (response.data.retry_suggested) {
          toast.error(`${errorMsg} You can try again.`, {
            duration: 6000
          });
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (err) {
      console.error("AI Module Creation Error:", err);
      let errorMessage = "An error occurred while generating the module";
      
      if (err.response?.status === 502) {
        errorMessage = "AI service temporarily unavailable. Please try again in a few minutes.";
      } else if (err.response?.status === 422) {
        // Handle validation errors from Pydantic
        if (err.response.data?.detail) {
          if (Array.isArray(err.response.data.detail)) {
            // Extract error messages from validation error array
            errorMessage = err.response.data.detail.map(e => `${e.loc?.join('.') || 'Field'}: ${e.msg || e.message || 'Validation error'}`).join('; ');
          } else if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else {
            errorMessage = "Validation error occurred";
          }
        }
      } else if (err.response?.data?.detail) {
        // Handle other API errors
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else {
          errorMessage = "API error occurred";
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Ensure errorMessage is always a string
      if (typeof errorMessage !== 'string') {
        errorMessage = "An error occurred while generating the module";
      }
      
      setError(errorMessage);
      toast.error(errorMessage, {
        duration: 6000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfigEdit = (section, field, value) => {
    setEditableConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const toggleEditing = () => {
    if (!isEditing && generatedConfig?.generated_config) {
      // When entering edit mode, ensure editableConfig is populated with generated data
      const deepCopy = JSON.parse(JSON.stringify(generatedConfig.generated_config));
      console.log('Setting editableConfig to:', deepCopy);
      setEditableConfig(deepCopy);
    }
    setIsEditing(!isEditing);
    console.log('Toggle editing - isEditing will be:', !isEditing);
  };

  const saveChanges = () => {
    setGeneratedConfig(prev => ({
      ...prev,
      generated_config: editableConfig
    }));
    setIsEditing(false);
    toast.success("Configuration updated successfully!");
  };

  const discardChanges = () => {
    setEditableConfig(JSON.parse(JSON.stringify(generatedConfig.generated_config)));
    setIsEditing(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const downloadConfig = () => {
    if (!generatedConfig) return;
    
    const dataStr = JSON.stringify(generatedConfig, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    // Use effective systems for filename
    const effectiveSourceSystem = generatedConfig.metadata?.source_system || (createNewSource ? newSourceName : sourceSystem);
    const effectiveTargetSystem = generatedConfig.metadata?.target_system || (createNewTarget ? newTargetName : targetSystem);
    link.download = `${moduleName}_${effectiveSourceSystem}_to_${effectiveTargetSystem}_config.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Configuration downloaded!");
  };

  const generateSQLScripts = async () => {
    if (!generatedConfig || !generatedConfig.generated_config) {
      toast.error("No configuration available to generate SQL");
      return;
    }

    setSqlLoading(true);
    setError(null);

    try {
      // Always use the current generatedConfig which includes all JSON editor changes
      const configToUse = generatedConfig.generated_config;
      
      // Convert AI-generated config to the format expected by generate-sql-scripts
      // Get source and target systems from metadata or from current state
      const effectiveSourceSystem = generatedConfig.metadata?.source_system || (createNewSource ? newSourceName : sourceSystem);
      const effectiveTargetSystem = generatedConfig.metadata?.target_system || (createNewTarget ? newTargetName : targetSystem);
      
      const sqlPayload = {
        sourceSystem: effectiveSourceSystem,
        targetSystem: effectiveTargetSystem,
        includedDependents: [],
        selectedDependentTargetIds: [],
        newDependents: [],
        generateScriptsOnly: true,
        aiGeneratedConfig: configToUse
      };

      console.log("Generating SQL with config:", configToUse); // Debug log

      const response = await axios.post(`${API}/generate-ai-sql-scripts`, sqlPayload);

      if (response.data) {
        setGeneratedSqlScripts(response.data.sqlScripts || response.data);
        toast.success("SQL scripts generated successfully!");
      } else {
        throw new Error("No SQL scripts returned");
      }
    } catch (err) {
      console.error("SQL Generation Error:", err);
      let errorMessage = "Failed to generate SQL scripts";
      
      if (err.response?.status === 422) {
        // Handle validation errors from Pydantic
        if (err.response.data?.detail) {
          if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail.map(e => `${e.loc?.join('.') || 'Field'}: ${e.msg || e.message || 'Validation error'}`).join('; ');
          } else if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          }
        }
      } else if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Ensure errorMessage is always a string
      if (typeof errorMessage !== 'string') {
        errorMessage = "Failed to generate SQL scripts";
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSqlLoading(false);
    }
  };

  const downloadSQL = () => {
    if (!generatedSqlScripts) return;
    
    const dataStr = generatedSqlScripts;
    const dataBlob = new Blob([dataStr], { type: 'text/sql' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${moduleName}_${sourceSystem}_to_${targetSystem}_migration.sql`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("SQL scripts downloaded!");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[80vh] overflow-y-auto bg-white">
        <DialogHeader className="pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            AI-Powered Module Creator
          </DialogTitle>
          <DialogDescription>
            Use AI to automatically generate migration configurations for new modules based on existing patterns
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {!generatedConfig ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Input Form */}
              <div className="space-y-4">
                <div className="p-6 border rounded-lg bg-white">
                  <h3 className="flex items-center gap-2 text-lg font-semibold mb-4">
                    <Wand2 className="h-4 w-4" />
                    Module Configuration
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Scenario Selection */}
                    <div className="space-y-3 p-4 border rounded-lg bg-blue-50">
                      <Label className="text-sm font-semibold text-blue-900">Scenario Selection</Label>
                      <div className="flex space-x-4">
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="scenario2"
                            name="scenario"
                            value="scenario2"
                            checked={scenario === "scenario2"}
                            onChange={(e) => setScenario(e.target.value)}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <label htmlFor="scenario2" className="text-sm text-gray-700">
                            Add Module to Existing Systems
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="scenario3"
                            name="scenario"
                            value="scenario3"
                            checked={scenario === "scenario3"}
                            onChange={(e) => setScenario(e.target.value)}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <label htmlFor="scenario3" className="text-sm text-gray-700">
                            Create New Connector
                          </label>
                        </div>
                      </div>
                      <p className="text-xs text-blue-700">
                        {scenario === "scenario2" 
                          ? "Create new modules for existing source/target systems using reference pairs"
                          : "Define entirely new source and/or target connectors"
                        }
                      </p>
                    </div>

                    {scenario === "scenario2" ? (
                      /* Scenario 2: Existing Systems */
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="sourceSystem">Source System *</Label>
                          <Select value={sourceSystem} onValueChange={setSourceSystem}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select source system" />
                            </SelectTrigger>
                            <SelectContent>
                              {getUniqueSources().map(source => (
                                <SelectItem key={source} value={source}>{source}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="targetSystem">Target System *</Label>
                          <Select value={targetSystem} onValueChange={setTargetSystem}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select target system" />
                            </SelectTrigger>
                            <SelectContent>
                              {getUniqueTargets().map(target => (
                                <SelectItem key={target} value={target}>{target}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="referencePair">Reference Pair *</Label>
                          <Select 
                            value={referencePair} 
                            onValueChange={setReferencePair}
                            disabled={!sourceSystem || !targetSystem}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select reference pair" />
                            </SelectTrigger>
                            <SelectContent>
                              {referencePairs
                                .filter(pair => 
                                  pair.source === sourceSystem && pair.target === targetSystem
                                )
                                .map(pair => (
                                  <SelectItem key={pair.id} value={pair.id.toString()}>
                                    {pair.label}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500">
                            Reference pair to use as template (matching your source and target)
                          </p>
                        </div>
                      </>
                    ) : (
                      /* Scenario 3: New Connector */
                      <>
                        {/* Source System Configuration */}
                        <div className="space-y-3 p-4 border rounded-lg bg-green-50">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold text-green-900">Source System</Label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id="createNewSource"
                                checked={createNewSource}
                                onChange={(e) => setCreateNewSource(e.target.checked)}
                                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                              />
                              <label htmlFor="createNewSource" className="text-xs text-green-700">
                                Create New Source
                              </label>
                            </div>
                          </div>
                          
                          {createNewSource ? (
                            <>
                              <div className="space-y-2">
                                <Label>New Source System Name *</Label>
                                <Input
                                  value={newSourceName}
                                  onChange={(e) => setNewSourceName(e.target.value)}
                                  placeholder="e.g., MyCustomAPI, NewServiceDesk"
                                />
                              </div>
                              
                              {/* Source Authentication */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <Label className="text-xs">Vendor ID *</Label>
                                  <Input
                                    value={sourceAuth.vendorId}
                                    onChange={(e) => setSourceAuth({...sourceAuth, vendorId: e.target.value})}
                                    placeholder="vendor_id"
                                    className="text-xs"
                                  />
                                </div>
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <Label className="text-xs">API Model * (JSON)</Label>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openJsonEditor("Source API Model", sourceAuth.apimodel || {}, "source_auth", "apimodel")}
                                      className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                                    >
                                      <Edit3 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <div className="p-2 border rounded bg-gray-50 text-xs min-h-[32px] overflow-auto">
                                    {displayJson(sourceAuth.apimodel, {})}
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs">Auth Type *</Label>
                                  <Select 
                                    value={sourceAuth.authType} 
                                    onValueChange={(value) => setSourceAuth({...sourceAuth, authType: value})}
                                  >
                                    <SelectTrigger className="text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="apikey">API Key</SelectItem>
                                      <SelectItem value="bearer">Bearer Token</SelectItem>
                                      <SelectItem value="basic">Basic Auth</SelectItem>
                                      <SelectItem value="oauth">OAuth</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="space-y-2">
                              <Label>Existing Source System *</Label>
                              <Select value={sourceSystem} onValueChange={setSourceSystem}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select existing source system" />
                                </SelectTrigger>
                                <SelectContent>
                                  {getUniqueSources().map(source => (
                                    <SelectItem key={source} value={source}>{source}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>

                        {/* Target System Configuration */}
                        <div className="space-y-3 p-4 border rounded-lg bg-purple-50">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold text-purple-900">Target System</Label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id="createNewTarget"
                                checked={createNewTarget}
                                onChange={(e) => setCreateNewTarget(e.target.checked)}
                                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                              />
                              <label htmlFor="createNewTarget" className="text-xs text-purple-700">
                                Create New Target
                              </label>
                            </div>
                          </div>
                          
                          {createNewTarget ? (
                            <>
                              <div className="space-y-2">
                                <Label>New Target System Name *</Label>
                                <Input
                                  value={newTargetName}
                                  onChange={(e) => setNewTargetName(e.target.value)}
                                  placeholder="e.g., MyTargetAPI, CustomSystem"
                                />
                              </div>
                              
                              {/* Target Authentication */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <Label className="text-xs">Vendor ID *</Label>
                                  <Input
                                    value={targetAuth.vendorId}
                                    onChange={(e) => setTargetAuth({...targetAuth, vendorId: e.target.value})}
                                    placeholder="vendor_id"
                                    className="text-xs"
                                  />
                                </div>
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <Label className="text-xs">API Model * (JSON)</Label>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openJsonEditor("Target API Model", targetAuth.apimodel || {}, "target_auth", "apimodel")}
                                      className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                                    >
                                      <Edit3 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <div className="p-2 border rounded bg-gray-50 text-xs min-h-[32px] overflow-auto">
                                    {displayJson(targetAuth.apimodel, {})}
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs">Auth Type *</Label>
                                  <Select 
                                    value={targetAuth.authType} 
                                    onValueChange={(value) => setTargetAuth({...targetAuth, authType: value})}
                                  >
                                    <SelectTrigger className="text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="apikey">API Key</SelectItem>
                                      <SelectItem value="bearer">Bearer Token</SelectItem>
                                      <SelectItem value="basic">Basic Auth</SelectItem>
                                      <SelectItem value="oauth">OAuth</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              
                              {/* Dependent Targets for New Target */}
                              <div className="space-y-3 border-t pt-4">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm font-medium">Dependent Targets</Label>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setNewTargetData({name: "", sourceKeyForChild: "", targetUniqueIdentifier: "", type: "dependent"});
                                      setShowAddDependentTargetModal(true);
                                    }}
                                    className="h-8 px-3 text-xs"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add
                                  </Button>
                                </div>
                                {customDependentTargets.length > 0 && (
                                  <div className="space-y-2">
                                    {customDependentTargets.map((target, index) => (
                                      <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded border">
                                        <div className="flex-1">
                                          <div className="text-sm font-medium">{target.name}</div>
                                          <div className="text-xs text-gray-500">Key: {target.sourceKeyForChild}</div>
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => {
                                            setCustomDependentTargets(prev => prev.filter((_, i) => i !== index));
                                          }}
                                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              {/* Mapping Targets for New Target */}
                              <div className="space-y-3 border-t pt-4">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm font-medium">Mapping Targets</Label>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setNewTargetData({name: "", sourceKeyForChild: "", targetUniqueIdentifier: "", type: "mapping"});
                                      setShowAddMappingTargetModal(true);
                                    }}
                                    className="h-8 px-3 text-xs"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add
                                  </Button>
                                </div>
                                {customMappingTargets.length > 0 && (
                                  <div className="space-y-2">
                                    {customMappingTargets.map((target, index) => (
                                      <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border">
                                        <div className="flex-1">
                                          <div className="text-sm font-medium">{target.name}</div>
                                          <div className="text-xs text-gray-500">Key: {target.sourceKeyForChild}</div>
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => {
                                            setCustomMappingTargets(prev => prev.filter((_, i) => i !== index));
                                          }}
                                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="space-y-2">
                              <Label>Existing Target System *</Label>
                              <Select value={targetSystem} onValueChange={setTargetSystem}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select existing target system" />
                                </SelectTrigger>
                                <SelectContent>
                                  {getUniqueTargets().map(target => (
                                    <SelectItem key={target} value={target}>{target}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>

                        {/* Optional Reference Pair for Scenario 3 */}
                        {(!createNewSource || !createNewTarget) && (
                          <div className="space-y-2">
                            <Label htmlFor="referencePair">Reference Pair (Optional)</Label>
                            <Select 
                              value={referencePair} 
                              onValueChange={setReferencePair}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select reference pair (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No reference pair</SelectItem>
                                {referencePairs.map(pair => (
                                  <SelectItem key={pair.id} value={pair.id.toString()}>
                                    {pair.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">
                              Optional: Use an existing pair as template for non-new systems
                            </p>
                          </div>
                        )}

                        {/* Mapping Targets for Existing Target */}
                        {!createNewTarget && targetSystem && (
                          <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm font-medium">Mapping Targets</Label>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setNewTargetData({name: "", sourceKeyForChild: "", targetUniqueIdentifier: "", type: "mapping"});
                                  setShowAddMappingTargetModal(true);
                                }}
                                className="h-8 px-3 text-xs"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add More
                              </Button>
                            </div>
                            
                            {/* Existing Mapping Targets from Reference Pair */}
                            {referencePair && referencePair !== 'none' && (
                              <div className="space-y-2">
                                <Label className="text-xs font-medium text-blue-700">
                                  Existing Mapping Targets from Reference Pair
                                </Label>
                                {loadingMappingTargetsPreview ? (
                                  <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                                    Loading existing mapping targets...
                                  </div>
                                ) : existingMappingTargetsPreview.length > 0 ? (
                                  <div className="space-y-1">
                                    {existingMappingTargetsPreview.map((target, index) => (
                                      <div key={index} className="flex items-center p-2 bg-blue-50 rounded border">
                                        <div className="flex-1">
                                          <div className="text-sm font-medium text-blue-800">{target.name}</div>
                                          <div className="text-xs text-blue-600">Type: {target.type} (from database)</div>
                                        </div>
                                        <div className="text-xs text-blue-500">✓ Included</div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                                    No existing mapping targets found for this reference pair
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Additional Custom Mapping Targets */}
                            {customMappingTargets.length > 0 && (
                              <div className="space-y-2">
                                <Label className="text-xs font-medium text-green-700">
                                  Additional Custom Mapping Targets
                                </Label>
                                <div className="space-y-1">
                                  {customMappingTargets.map((target, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border">
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-green-800">{target.name}</div>
                                        <div className="text-xs text-green-600">Key: {target.sourceKeyForChild || 'N/A'} (custom)</div>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          setCustomMappingTargets(prev => prev.filter((_, i) => i !== index));
                                        }}
                                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Helper Text */}
                            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                              <strong>Note:</strong> Existing mapping targets from the reference pair will be automatically included. 
                              Add additional mapping targets as needed using the "+Add More" button above.
                            </div>
                          </div>
                        )}

                        {/* Source Filter Options for New Sources - Only show when Create New Source is checked */}
                        {createNewSource && (
                          <div className="space-y-3 p-4 border rounded-lg bg-gray-50">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm font-semibold text-gray-900">Source Filter Options</Label>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={addSourceFilterOption}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Option
                              </Button>
                            </div>
                            <p className="text-xs text-gray-600">Define query parameters for source API calls</p>
                            
                            {sourceFilterOptions.map((option, index) => (
                              <div key={index} className="flex gap-2 items-center">
                                <Input
                                  placeholder="Parameter name"
                                  value={option.name}
                                  onChange={(e) => updateSourceFilterOption(index, "name", e.target.value)}
                                  className="text-xs"
                                />
                                <Input
                                  placeholder="Default value"
                                  value={option.value}
                                  onChange={(e) => updateSourceFilterOption(index, "value", e.target.value)}
                                  className="text-xs"
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeSourceFilterOption(index)}
                                  className="text-red-600"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="moduleName">Module Name *</Label>
                      <Input
                        id="moduleName"
                        value={moduleName}
                        onChange={(e) => setModuleName(e.target.value)}
                        placeholder="e.g., Problems, Incidents, Users"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="moduleDescription">Module Description</Label>
                      <Textarea
                        id="moduleDescription"
                        value={moduleDescription}
                        onChange={(e) => setModuleDescription(e.target.value)}
                        placeholder="Optional: Describe what this module handles"
                        rows={3}
                      />
                    </div>

                    {/* Dependent Targets Selection */}
                    {referencePair && availableDependents.length > 0 && (
                      <div className="space-y-3 p-4 border rounded-lg bg-blue-50">
                        <Label className="text-sm font-semibold text-blue-900">Dependent Targets</Label>
                        <p className="text-xs text-blue-700">Select which dependent targets to include:</p>
                        
                        <div className="space-y-2">
                          {availableDependents.map((dep) => (
                            <div key={dep.name} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`dep-${dep.name}`}
                                checked={selectedDependents.includes(dep.name)}
                                onChange={() => handleToggleDependent(dep.name)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <label htmlFor={`dep-${dep.name}`} className="text-sm text-gray-700">
                                {dep.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Custom Dependent Targets */}
                    {referencePair && (
                      <div className="space-y-3 p-4 border rounded-lg bg-green-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm font-semibold text-green-900">Custom Dependent Targets</Label>
                            <p className="text-xs text-green-700">Add additional dependent targets (AI will generate based on template)</p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setShowAddDependentModal(true)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        </div>
                        
                        {customDependents.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {customDependents.map((dep) => (
                              <div key={dep} className="flex items-center gap-1 px-3 py-1 bg-green-100 rounded-full text-xs">
                                <span>{dep}</span>
                                <button
                                  onClick={() => handleRemoveCustomDependent(dep)}
                                  className="ml-1 text-green-700 hover:text-green-900"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <Button 
                      onClick={handleGenerateModule} 
                      disabled={
                        loading || !moduleName ||
                        (scenario === "scenario2" && (!sourceSystem || !targetSystem || !referencePair)) ||
                        (scenario === "scenario3" && (
                          (!createNewSource && !sourceSystem) ||
                          (!createNewTarget && !targetSystem) ||
                          (createNewSource && (!newSourceName || !sourceAuth.vendorId || !sourceAuth.apimodel)) ||
                          (createNewTarget && (!newTargetName || !targetAuth.vendorId || !targetAuth.apimodel))
                        ))
                      }
                      className="w-full mt-6 bg-purple-600 hover:bg-purple-700"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generating with AI...
                        </>
                      ) : (
                        <>
                          <Bot className="mr-2 h-4 w-4" />
                          Generate Module with AI
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* How It Works */}
              <div className="space-y-4">
                <div className="p-6 border rounded-lg bg-white">
                  <h3 className="text-lg font-semibold mb-4">How It Works</h3>
                  {scenario === "scenario2" ? (
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600 mt-0.5">1</div>
                        <div>
                          <p className="font-medium">Select Systems</p>
                          <p className="text-gray-600">Choose your source and target systems from existing configurations</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600 mt-0.5">2</div>
                        <div>
                          <p className="font-medium">Reference Template</p>
                          <p className="text-gray-600">Select a reference pair to use as template for the new module</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600 mt-0.5">3</div>
                        <div>
                          <p className="font-medium">AI Generation</p>
                          <p className="text-gray-600">AI analyzes existing patterns and generates complete migration configurations</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600 mt-0.5">4</div>
                        <div>
                          <p className="font-medium">Review & Deploy</p>
                          <p className="text-gray-600">Review and edit all database fields before generating SQL</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-600 mt-0.5">1</div>
                        <div>
                          <p className="font-medium">Define Connectors</p>
                          <p className="text-gray-600">Create new source/target systems or select existing ones</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-600 mt-0.5">2</div>
                        <div>
                          <p className="font-medium">Configure Authentication</p>
                          <p className="text-gray-600">Set up auth details and API parameters for new connectors</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-600 mt-0.5">3</div>
                        <div>
                          <p className="font-medium">AI Construction</p>
                          <p className="text-gray-600">AI builds complete configurations from scratch using Scenario 3 rules</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-600 mt-0.5">4</div>
                        <div>
                          <p className="font-medium">Generate & Deploy</p>
                          <p className="text-gray-600">Review configurations and generate SQL for new connector deployment</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className={`mt-6 p-4 rounded-lg ${scenario === "scenario2" ? "bg-blue-50" : "bg-green-50"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className={`h-4 w-4 ${scenario === "scenario2" ? "text-blue-600" : "text-green-600"}`} />
                      <span className={`font-medium ${scenario === "scenario2" ? "text-blue-900" : "text-green-900"}`}>
                        AI Features - {scenario === "scenario2" ? "Scenario 2" : "Scenario 3"}
                      </span>
                    </div>
                    {scenario === "scenario2" ? (
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Analyzes existing migration patterns</li>
                        <li>• Uses reference pairs as templates</li>
                        <li>• Generates proper JSON structures</li>
                        <li>• Creates realistic field mappings</li>
                        <li>• Follows system-specific conventions</li>
                      </ul>
                    ) : (
                      <ul className="text-sm text-green-800 space-y-1">
                        <li>• Constructs configurations from scratch</li>
                        <li>• Builds authentication & validation tables</li>
                        <li>• Creates API executors and mappings</li>
                        <li>• Generates complete source filter options</li>
                        <li>• AI-powered field mapping suggestions</li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-medium">AI-Generated Database Configuration</h3>
                </div>
              </div>
              
              {/* Tabs Navigation */}
              <Tabs defaultValue="entities" className="w-full">
                <TabsList className="grid w-full grid-cols-7 mb-4">
                  <TabsTrigger value="entities">Migration Entities</TabsTrigger>
                  <TabsTrigger value="sources">Migration Sources</TabsTrigger>
                  <TabsTrigger value="targets">Migration Targets</TabsTrigger>
                  <TabsTrigger value="connection">Connection Fields</TabsTrigger>
                  <TabsTrigger value="validate">Validate Data</TabsTrigger>
                  <TabsTrigger value="auth">Validate Auth</TabsTrigger>
                  <TabsTrigger value="filter">Source Filter</TabsTrigger>
                </TabsList>
                
                <TabsContent value="entities" className="space-y-4">
                  {/* MIGRATION_ENTITIES Table */}
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-base text-gray-800 border-b pb-2">🔗 Migration Entities Table</h4>
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <Button 
                              onClick={saveChanges}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Save Changes
                            </Button>
                            <Button 
                              onClick={discardChanges}
                              variant="outline"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button 
                              onClick={toggleEditing}
                              variant="outline" 
                              size="sm"
                            >
                              <Edit3 className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                            <Button 
                              onClick={() => setGeneratedConfig(null)}
                              variant="outline"
                              size="sm"
                            >
                              Create New Module
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      {/* String Fields */}
                      {[
                        ['source', 'Source'],
                        ['target', 'Target'],
                        ['entity', 'Entity'],
                        ['customFieldKey', 'Custom Field Key'],
                        ['delimiter', 'Delimiter']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                          {isEditing ? (
                            <Input 
                              value={editableConfig?.migration_entities?.[field] || ''} 
                              onChange={(e) => handleConfigEdit('migration_entities', field, e.target.value)}
                              className="mt-1 text-xs h-8"
                            />
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                              {generatedConfig.generated_config.migration_entities?.[field] || 'Not set'}
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Boolean Fields */}
                      {[
                        ['isDelimiterEnabled', 'Delimiter Enabled'],
                        ['selectMultipleEntities', 'Select Multiple Entities']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                          {isEditing ? (
                            <select 
                              value={editableConfig?.migration_entities?.[field] ? 'true' : 'false'} 
                              onChange={(e) => handleConfigEdit('migration_entities', field, e.target.value === 'true')}
                              className="mt-1 text-xs h-8 border rounded px-2 w-full"
                            >
                              <option value="false">False</option>
                              <option value="true">True</option>
                            </select>
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                              {generatedConfig.generated_config.migration_entities?.[field] ? 'True' : 'False'}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Number/ID Fields */}
                      {[
                        ['migrationObject', 'Migration Object ID'],
                        ['mainTarget', 'Main Target ID']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                          {isEditing ? (
                            <Input 
                              type="number"
                              value={editableConfig?.migration_entities?.[field] || ''} 
                              onChange={(e) => handleConfigEdit('migration_entities', field, parseInt(e.target.value) || null)}
                              className="mt-1 text-xs h-8"
                            />
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                              {generatedConfig.generated_config.migration_entities?.[field] || 'Not set'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* JSON/Array Fields for Migration Entities */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        ['sourceField', 'Source Field'],
                        ['targetField', 'Target Field'],
                        ['dependentTargets', 'Dependent Targets'],
                        ['mappingTargets', 'Mapping Targets'],
                        ['incompatible', 'Incompatible'],
                        ['sourceExtender', 'Source Extender']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-gray-700">{label} (JSON)</Label>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openJsonEditor(label, generatedConfig.generated_config.migration_entities?.[field], 'migration_entities', field)}
                              className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                            >
                              <Edit3 className="h-3 w-3" />
                            </Button>
                          </div>
                          {isEditing ? (
                            <Textarea
                              value={JSON.stringify(editableConfig?.migration_entities?.[field] || [], null, 2)}
                              onChange={(e) => {
                                try {
                                  const parsed = JSON.parse(e.target.value);
                                  handleConfigEdit('migration_entities', field, parsed);
                                } catch (err) {
                                  // Invalid JSON, don't update yet
                                }
                              }}
                              className="mt-1 text-xs h-24 font-mono"
                              placeholder='[]'
                            />
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs h-24 overflow-auto font-mono">
                              {displayJson(generatedConfig.generated_config.migration_entities?.[field], [])}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="sources" className="space-y-4">
                  {/* MIGRATION_SOURCES Table */}
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-base text-gray-800 border-b pb-2">📊 Migration Sources Table</h4>
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <Button 
                              onClick={saveChanges}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Save Changes
                            </Button>
                            <Button 
                              onClick={discardChanges}
                              variant="outline"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button 
                              onClick={toggleEditing}
                              variant="outline" 
                              size="sm"
                            >
                              <Edit3 className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      {/* String Fields - CSV ONLY */}
                      {[
                        ['provider', 'Provider'],
                        ['source', 'Source'],
                        ['sourceUniqueIdentifier', 'Source Unique Identifier'],
                        ['sourceAuthType', 'Source Auth Type'],
                        ['paginationMethod', 'Pagination Method'],
                        ['source_rootMapping', 'Source Root Mapping'],
                        ['source_response_type', 'Source Response Type'],
                        ['migrationEntity', 'Migration Entity']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                          {isEditing ? (
                            <Input 
                              value={(() => {
                                const value = editableConfig?.migration_sources?.[field] || '';
                                console.log(`Field ${field} value:`, value, 'editableConfig:', editableConfig?.migration_sources);
                                return value;
                              })()} 
                              onChange={(e) => handleConfigEdit('migration_sources', field, e.target.value)}
                              className="mt-1 text-xs h-8"
                            />
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                              {generatedConfig.generated_config.migration_sources?.[field] || 'Not set'}
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Number Field - CSV ONLY */}
                      <div>
                        <Label className="text-xs font-semibold text-gray-700">Batch Size</Label>
                        {isEditing ? (
                          <Input 
                            type="number"
                            value={editableConfig?.migration_sources?.batchSize || ''} 
                            onChange={(e) => handleConfigEdit('migration_sources', 'batchSize', parseInt(e.target.value) || null)}
                            className="mt-1 text-xs h-8"
                          />
                        ) : (
                          <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                            {generatedConfig.generated_config.migration_sources?.batchSize || 'Not set'}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* JSON Fields for Migration Sources - CSV ONLY */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        ['sourceMappingExecutor', 'Source Mapping Executor'],
                        ['sourceExecutor', 'Source Executor'],
                        ['sourceQueryParamsOptions', 'Source Query Params Options'],
                        ['sourceResponseAttributesExecutor', 'Source Response Attributes Executor'],
                        ['targetMappingExecutor', 'Target Mapping Executor'],
                        ['additionalDetails', 'Additional Details'],
                        ['sourceMappingConfig', 'Source Mapping Config'],
                        ['paginationFields', 'Pagination Fields']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-gray-700">{label} (JSON)</Label>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openJsonEditor(label, generatedConfig.generated_config.migration_sources?.[field], 'migration_sources', field)}
                              className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                            >
                              <Edit3 className="h-3 w-3" />
                            </Button>
                          </div>
                          {isEditing ? (
                            <Textarea
                              value={JSON.stringify(editableConfig?.migration_sources?.[field] || (field.includes('Fields') ? [] : {}), null, 2)}
                              onChange={(e) => {
                                try {
                                  const parsed = JSON.parse(e.target.value);
                                  handleConfigEdit('migration_sources', field, parsed);
                                } catch (err) {
                                  // Invalid JSON, don't update yet
                                }
                              }}
                              className="mt-1 text-xs h-20 font-mono"
                              placeholder={field.includes('Fields') ? '[]' : '{"key": "value"}'}
                            />
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs h-20 overflow-auto font-mono">
                              {displayJson(generatedConfig.generated_config.migration_sources?.[field], field.includes('Fields') ? [] : {})}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="targets" className="space-y-4">
                  {/* MIGRATION_TARGETS Table - Main Target */}
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-base text-gray-800">🎯 Migration Targets - Main Target</h4>
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <Button 
                              onClick={saveChanges}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Save Changes
                            </Button>
                            <Button 
                              onClick={discardChanges}
                              variant="outline"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button 
                            onClick={toggleEditing}
                            variant="outline" 
                            size="sm"
                          >
                            <Edit3 className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      {/* String Fields - CSV ONLY (matching SQL INSERT fields) */}
                      {[
                        ['name', 'Name'],
                        ['target_type', 'Target Type'],
                        ['target_response_type', 'Target Response Type'],
                        ['targetUniqueIdentifier', 'Target Unique Identifier'],
                        ['source', 'Source'],
                        ['mappingDisplayField', 'Mapping Display Field']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                          {isEditing ? (
                            <Input
                              value={editableConfig?.migration_targets?.mainTarget?.[field] || ''}
                              onChange={(e) => {
                                const newConfig = {...editableConfig};
                                if (!newConfig.migration_targets) newConfig.migration_targets = { mainTarget: {}, dependentTargets: [] };
                                if (!newConfig.migration_targets.mainTarget) newConfig.migration_targets.mainTarget = {};
                                newConfig.migration_targets.mainTarget[field] = e.target.value;
                                setEditableConfig(newConfig);
                              }}
                              className="mt-1 text-xs h-8"
                            />
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                              {generatedConfig.generated_config.migration_targets?.mainTarget?.[field] || 'Not set'}
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Boolean Fields - CSV ONLY */}
                      {[
                        ['isMappingTarget', 'Is Mapping Target'],
                        ['isMainTarget', 'Is Main Target']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                          {isEditing ? (
                            <select
                              value={editableConfig?.migration_targets?.mainTarget?.[field] ? 'true' : 'false'}
                              onChange={(e) => {
                                const newConfig = {...editableConfig};
                                if (!newConfig.migration_targets) newConfig.migration_targets = { mainTarget: {}, dependentTargets: [] };
                                if (!newConfig.migration_targets.mainTarget) newConfig.migration_targets.mainTarget = {};
                                newConfig.migration_targets.mainTarget[field] = e.target.value === 'true';
                                setEditableConfig(newConfig);
                              }}
                              className="mt-1 text-xs h-8 border rounded px-2 w-full"
                            >
                              <option value="false">False</option>
                              <option value="true">True</option>
                            </select>
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                              {generatedConfig.generated_config.migration_targets?.mainTarget?.[field] ? 'True' : 'False'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* JSON Fields for Main Target - CSV ONLY */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        ['fieldMappings', 'Field Mappings'],
                        ['targetExecutor', 'Target Executor'],
                        ['additionalDetails', 'Additional Details']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-gray-700">{label} (JSON)</Label>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openJsonEditor(label, generatedConfig.generated_config.migration_targets?.mainTarget?.[field], 'migration_targets.mainTarget', field)}
                              className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300"
                            >
                              <Edit3 className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                          </div>
                          <div className="mt-1 p-2 border rounded bg-gray-50 text-xs h-24 overflow-auto font-mono">
                            {displayJson(generatedConfig.generated_config.migration_targets?.mainTarget?.[field], field === 'fieldMappings' ? [] : {})}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* MIGRATION_TARGETS Table - Dependent Targets */}
                  {generatedConfig.generated_config.migration_targets?.dependentTargets?.length > 0 && (
                    <div className="border rounded-lg p-4 bg-white">
                      <h4 className="font-semibold text-base text-gray-800 mb-4 border-b pb-2">🎯 Migration Targets - Dependent Targets ({generatedConfig.generated_config.migration_targets.dependentTargets.length})</h4>
                      {generatedConfig.generated_config.migration_targets.dependentTargets.map((depTarget, idx) => (
                        <div key={idx} className="mb-6 pb-4 border-b last:border-b-0">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-semibold text-sm text-blue-700">Dependent Target #{idx + 1}: {depTarget.name || 'Unnamed'}</h5>
                            <div className="flex gap-2">
                              {editingDependentIndex === idx ? (
                                <>
                                  <Button 
                                    onClick={() => {
                                      setGeneratedConfig(editableConfig);
                                      setEditingDependentIndex(null);
                                      toast.success("Changes saved!");
                                    }}
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Save
                                  </Button>
                                  <Button 
                                    onClick={() => {
                                      setEditableConfig(JSON.parse(JSON.stringify(generatedConfig)));
                                      setEditingDependentIndex(null);
                                    }}
                                    variant="outline"
                                    size="sm"
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <Button 
                                  onClick={() => {
                                    setEditableConfig(JSON.parse(JSON.stringify(generatedConfig)));
                                    setEditingDependentIndex(idx);
                                  }}
                                  variant="outline" 
                                  size="sm"
                                >
                                  <Edit3 className="mr-2 h-4 w-4" />
                                  Edit
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            {/* String Fields - CSV ONLY (matching SQL INSERT fields) */}
                            {[
                              ['name', 'Name'],
                              ['target_type', 'Target Type'],
                              ['target_response_type', 'Target Response Type'],
                              ['targetUniqueIdentifier', 'Target Unique Identifier'],
                              ['sourceKeyForChild', 'Source Key For Child'],
                              ['source', 'Source'],
                              ['mappingDisplayField', 'Mapping Display Field']
                            ].map(([field, label]) => (
                              <div key={field}>
                                <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                                {editingDependentIndex === idx ? (
                                  <Input
                                    value={editableConfig?.generated_config?.migration_targets?.dependentTargets?.[idx]?.[field] || ''}
                                    onChange={(e) => {
                                      const newConfig = {...editableConfig};
                                      if (!newConfig.generated_config.migration_targets.dependentTargets[idx]) {
                                        newConfig.generated_config.migration_targets.dependentTargets[idx] = {};
                                      }
                                      newConfig.generated_config.migration_targets.dependentTargets[idx][field] = e.target.value;
                                      setEditableConfig(newConfig);
                                    }}
                                    className="mt-1 text-xs h-8"
                                  />
                                ) : (
                                  <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                                    {depTarget[field] || 'Not set'}
                                  </div>
                                )}
                              </div>
                            ))}
                            
                            {/* Boolean Fields - CSV ONLY */}
                            {[
                              ['isMappingTarget', 'Is Mapping Target'],
                              ['isMainTarget', 'Is Main Target']
                            ].map(([field, label]) => (
                              <div key={field}>
                                <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                                {editingDependentIndex === idx ? (
                                  <select
                                    value={editableConfig?.generated_config?.migration_targets?.dependentTargets?.[idx]?.[field] || 'false'}
                                    onChange={(e) => {
                                      const newConfig = {...editableConfig};
                                      if (!newConfig.generated_config.migration_targets.dependentTargets[idx]) {
                                        newConfig.generated_config.migration_targets.dependentTargets[idx] = {};
                                      }
                                      newConfig.generated_config.migration_targets.dependentTargets[idx][field] = e.target.value === 'true';
                                      setEditableConfig(newConfig);
                                    }}
                                    className="mt-1 w-full border rounded px-2 text-xs h-8"
                                  >
                                    <option value="false">False</option>
                                    <option value="true">True</option>
                                  </select>
                                ) : (
                                  <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                                    {depTarget[field] ? 'True' : 'False'}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          {/* JSON Fields - CSV ONLY */}
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                              ['fieldMappings', 'Field Mappings'],
                              ['targetExecutor', 'Target Executor'],
                              ['additionalDetails', 'Additional Details']
                            ].map(([field, label]) => (
                              <div key={field}>
                                <div className="flex items-center justify-between mb-1">
                                  <Label className="text-xs font-semibold text-gray-700">{label} (JSON)</Label>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openJsonEditor(label, depTarget[field], 'migration_targets', field, idx)}
                                    className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300"
                                  >
                                    <Edit3 className="h-3 w-3 mr-1" />
                                    Edit
                                  </Button>
                                </div>
                                <div className="mt-1 p-2 border rounded bg-gray-50 text-xs h-24 overflow-auto font-mono">
                                  {displayJson(depTarget[field], field === 'fieldMappings' ? [] : {})}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* MIGRATION_TARGETS Table - Mapping Targets */}
                  {generatedConfig.generated_config.migration_targets?.mappingTargets?.length > 0 && (
                    <div className="border rounded-lg p-4 bg-white">
                      <h4 className="font-semibold text-base text-gray-800 mb-4 border-b pb-2">🗺️ Migration Targets - Mapping Targets ({generatedConfig.generated_config.migration_targets.mappingTargets.length})</h4>
                      {generatedConfig.generated_config.migration_targets.mappingTargets.map((mapTarget, idx) => (
                        <div key={idx} className="mb-6 pb-4 border-b last:border-b-0">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-semibold text-sm text-green-700">Mapping Target #{idx + 1}: {mapTarget.name || 'Unnamed'}</h5>
                            <div className="flex gap-2">
                              {editingMappingIndex === idx ? (
                                <>
                                  <Button 
                                    onClick={() => {
                                      setGeneratedConfig(editableConfig);
                                      setEditingMappingIndex(null);
                                      toast.success("Changes saved!");
                                    }}
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Save
                                  </Button>
                                  <Button 
                                    onClick={() => {
                                      setEditableConfig(JSON.parse(JSON.stringify(generatedConfig)));
                                      setEditingMappingIndex(null);
                                    }}
                                    variant="outline"
                                    size="sm"
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <Button 
                                  onClick={() => {
                                    setEditableConfig(JSON.parse(JSON.stringify(generatedConfig)));
                                    setEditingMappingIndex(idx);
                                  }}
                                  variant="outline" 
                                  size="sm"
                                >
                                  <Edit3 className="mr-2 h-4 w-4" />
                                  Edit
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            {/* String Fields - CSV ONLY (matching SQL INSERT fields) */}
                            {[
                              ['name', 'Name'],
                              ['target_type', 'Target Type'],
                              ['target_response_type', 'Target Response Type'],
                              ['targetUniqueIdentifier', 'Target Unique Identifier'],
                              ['sourceKeyForChild', 'Source Key For Child'],
                              ['source', 'Source'],
                              ['mappingDisplayField', 'Mapping Display Field']
                            ].map(([field, label]) => (
                              <div key={field}>
                                <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                                {editingMappingIndex === idx ? (
                                  <Input
                                    value={editableConfig?.generated_config?.migration_targets?.mappingTargets?.[idx]?.[field] || ''}
                                    onChange={(e) => {
                                      const newConfig = {...editableConfig};
                                      if (!newConfig.generated_config.migration_targets.mappingTargets[idx]) {
                                        newConfig.generated_config.migration_targets.mappingTargets[idx] = {};
                                      }
                                      newConfig.generated_config.migration_targets.mappingTargets[idx][field] = e.target.value;
                                      setEditableConfig(newConfig);
                                    }}
                                    className="mt-1 text-xs h-8"
                                  />
                                ) : (
                                  <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                                    {mapTarget[field] || 'Not set'}
                                  </div>
                                )}
                              </div>
                            ))}
                            
                            {/* Boolean Fields - CSV ONLY */}
                            {[
                              ['isMappingTarget', 'Is Mapping Target'],
                              ['isMainTarget', 'Is Main Target']
                            ].map(([field, label]) => (
                              <div key={field}>
                                <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                                {editingMappingIndex === idx ? (
                                  <select
                                    value={editableConfig?.generated_config?.migration_targets?.mappingTargets?.[idx]?.[field] || 'false'}
                                    onChange={(e) => {
                                      const newConfig = {...editableConfig};
                                      if (!newConfig.generated_config.migration_targets.mappingTargets[idx]) {
                                        newConfig.generated_config.migration_targets.mappingTargets[idx] = {};
                                      }
                                      newConfig.generated_config.migration_targets.mappingTargets[idx][field] = e.target.value === 'true';
                                      setEditableConfig(newConfig);
                                    }}
                                    className="mt-1 w-full border rounded px-2 text-xs h-8"
                                  >
                                    <option value="false">False</option>
                                    <option value="true">True</option>
                                  </select>
                                ) : (
                                  <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                                    {mapTarget[field] ? 'True' : 'False'}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          {/* JSON Fields - CSV ONLY */}
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                              ['fieldMappings', 'Field Mappings'],
                              ['targetExecutor', 'Target Executor'],
                              ['additionalDetails', 'Additional Details']
                            ].map(([field, label]) => (
                              <div key={field}>
                                <div className="flex items-center justify-between mb-1">
                                  <Label className="text-xs font-semibold text-gray-700">{label} (JSON)</Label>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openJsonEditor(label, mapTarget[field], 'migration_targets', field, `mapping_${idx}`)}
                                    className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300"
                                  >
                                    <Edit3 className="h-3 w-3 mr-1" />
                                    Edit
                                  </Button>
                                </div>
                                <div className="mt-1 p-2 border rounded bg-gray-50 text-xs h-24 overflow-auto font-mono">
                                  {displayJson(mapTarget[field], field === 'fieldMappings' ? [] : {})}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="connection" className="space-y-4">
                  {/* TARGET_CONNECTION_FIELDS Table */}
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-base text-gray-800">🔗 Target Connection Fields</h4>
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <Button 
                              onClick={saveChanges}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Save Changes
                            </Button>
                            <Button 
                              onClick={discardChanges}
                              variant="outline"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button 
                            onClick={toggleEditing}
                            variant="outline" 
                            size="sm"
                          >
                            <Edit3 className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      {[
                        ['source', 'Source'],
                        ['target', 'Target'],
                        ['authType', 'Auth Type'],
                        ['domainType', 'Domain Type']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                          {isEditing ? (
                            <Input
                              value={editableConfig?.target_connection_fields?.[field] || ''}
                              onChange={(e) => {
                                const newConfig = {...editableConfig};
                                if (!newConfig.target_connection_fields) newConfig.target_connection_fields = {};
                                newConfig.target_connection_fields[field] = e.target.value;
                                setEditableConfig(newConfig);
                              }}
                              className="mt-1 text-xs h-8"
                            />
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                              {generatedConfig.generated_config.target_connection_fields?.[field] || 'Not set'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* JSON Fields */}
                    <div className="mt-4 grid grid-cols-1 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-xs font-semibold text-gray-700">Fields (JSON)</Label>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openJsonEditor('Fields', generatedConfig.generated_config.target_connection_fields?.fields, 'target_connection_fields', 'fields')}
                            className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                          >
                            <Edit3 className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="mt-1 p-2 border rounded bg-gray-50 text-xs h-24 overflow-auto font-mono">
                          {displayJson(generatedConfig.generated_config.target_connection_fields?.fields, [])}
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="validate" className="space-y-4">
                  {/* VALIDATE_SOURCE_DATA Table */}
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-base text-gray-800">✓ Validate Source Data</h4>
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <Button 
                              onClick={saveChanges}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Save Changes
                            </Button>
                            <Button 
                              onClick={discardChanges}
                              variant="outline"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button 
                            onClick={toggleEditing}
                            variant="outline" 
                            size="sm"
                          >
                            <Edit3 className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      {[
                        ['source', 'Source'],
                        ['target', 'Target'],
                        ['entity', 'Entity'],
                        ['source_response_type', 'Source Response Type']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                          {isEditing ? (
                            <Input
                              value={editableConfig?.validate_source_data?.[field] || ''}
                              onChange={(e) => {
                                const newConfig = {...editableConfig};
                                if (!newConfig.validate_source_data) newConfig.validate_source_data = {};
                                newConfig.validate_source_data[field] = e.target.value;
                                setEditableConfig(newConfig);
                              }}
                              className="mt-1 text-xs h-8"
                            />
                          ) : (
                            <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
                              {generatedConfig.generated_config.validate_source_data?.[field] || 'Not set'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* JSON Fields */}
                    <div className="mt-4 grid grid-cols-1 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-xs font-semibold text-gray-700">Source Executor (JSON)</Label>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openJsonEditor('Source Executor', generatedConfig.generated_config.validate_source_data?.sourceExecutor, 'validate_source_data', 'sourceExecutor')}
                            className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                          >
                            <Edit3 className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="mt-1 p-2 border rounded bg-gray-50 text-xs h-24 overflow-auto font-mono">
                          {displayJson(generatedConfig.generated_config.validate_source_data?.sourceExecutor, {})}
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="auth" className="space-y-4">
                  {/* VALIDATE_AUTH Table */}
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-base text-gray-800">🔐 Validate Auth</h4>
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <Button 
                              onClick={saveChanges}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Save Changes
                            </Button>
                            <Button 
                              onClick={discardChanges}
                              variant="outline"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button 
                            onClick={toggleEditing}
                            variant="outline" 
                            size="sm"
                          >
                            <Edit3 className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {generatedConfig.generated_config.validate_auth && 
                       Object.keys(generatedConfig.generated_config.validate_auth).length > 0 ? (
                        Object.entries(generatedConfig.generated_config.validate_auth).map(([key, authData]) => (
                          <div key={key} className="border rounded p-3 bg-gray-50">
                            <h5 className="font-medium text-sm text-gray-700 mb-2 capitalize">{key.replace('_', ' ')}</h5>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                              {[
                                ['vendorId', 'Vendor ID'],
                                ['authType', 'Auth Type']
                              ].map(([field, label]) => (
                                <div key={field}>
                                  <Label className="text-xs font-semibold text-gray-700">{label}</Label>
                                  {isEditing ? (
                                    <Input
                                      value={editableConfig?.validate_auth?.[key]?.[field] || ''}
                                      onChange={(e) => {
                                        const newConfig = {...editableConfig};
                                        if (!newConfig.validate_auth) newConfig.validate_auth = {};
                                        if (!newConfig.validate_auth[key]) newConfig.validate_auth[key] = {};
                                        newConfig.validate_auth[key][field] = e.target.value;
                                        setEditableConfig(newConfig);
                                      }}
                                      className="mt-1 text-xs h-8"
                                    />
                                  ) : (
                                    <div className="mt-1 p-2 border rounded bg-white text-xs min-h-[32px]">
                                      {authData?.[field] || 'Not set'}
                                    </div>
                                  )}
                                </div>
                              ))}
                              
                              {/* API Model JSON Field */}
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <Label className="text-xs font-semibold text-gray-700">API Model (JSON)</Label>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openJsonEditor(`${key} API Model`, authData?.apimodel, 'validate_auth', `${key}.apimodel`)}
                                    className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                                  >
                                    <Edit3 className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="mt-1 p-2 border rounded bg-white text-xs h-20 overflow-auto font-mono">
                                  {displayJson(authData?.apimodel, {})}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-sm">No authentication data configured</p>
                          <p className="text-xs">This will be populated when new sources/targets are created</p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="filter" className="space-y-4">
                  {/* SOURCE_FILTER_OPTIONS Table */}
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-base text-gray-800">⚙️ Source Filter Options</h4>
                      <div className="flex gap-2">
                        {isEditing ? (
                          <>
                            <Button 
                              onClick={saveChanges}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Save Changes
                            </Button>
                            <Button 
                              onClick={discardChanges}
                              variant="outline"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button 
                            onClick={toggleEditing}
                            variant="outline" 
                            size="sm"
                          >
                            <Edit3 className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {generatedConfig.generated_config.source_filter_options && 
                       Object.keys(generatedConfig.generated_config.source_filter_options).length > 0 ? (
                        Object.entries(generatedConfig.generated_config.source_filter_options).map(([key, filterData]) => (
                          <div key={key} className="border rounded p-3 bg-gray-50">
                            <h5 className="font-medium text-sm text-gray-700 mb-2 capitalize">{key} Source Options</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <Label className="text-xs font-semibold text-gray-700">Name</Label>
                                {isEditing ? (
                                  <Input
                                    value={editableConfig?.source_filter_options?.[key]?.name || ''}
                                    onChange={(e) => {
                                      const newConfig = {...editableConfig};
                                      if (!newConfig.source_filter_options) newConfig.source_filter_options = {};
                                      if (!newConfig.source_filter_options[key]) newConfig.source_filter_options[key] = {};
                                      newConfig.source_filter_options[key].name = e.target.value;
                                      setEditableConfig(newConfig);
                                    }}
                                    className="mt-1 text-xs h-8"
                                  />
                                ) : (
                                  <div className="mt-1 p-2 border rounded bg-white text-xs min-h-[32px]">
                                    {filterData?.name || 'Not set'}
                                  </div>
                                )}
                              </div>
                              
                              {/* Query Params JSON Field */}
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <Label className="text-xs font-semibold text-gray-700">Query Params (JSON)</Label>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openJsonEditor(`${key} Query Params`, filterData?.queryParams, 'source_filter_options', `${key}.queryParams`)}
                                    className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                                  >
                                    <Edit3 className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="mt-1 p-2 border rounded bg-white text-xs h-20 overflow-auto font-mono">
                                  {displayJson(filterData?.queryParams, [])}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-sm">No source filter options configured</p>
                          <p className="text-xs">This will be populated when new sources are created</p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Generate SQL Button */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-sm">Ready to Generate SQL Scripts?</h4>
                    <p className="text-xs text-gray-600">Review the configuration above, then generate SQL INSERT scripts for database deployment.</p>
                  </div>
                  <Button 
                    onClick={generateSQLScripts}
                    disabled={sqlLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sqlLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating SQL...
                      </>
                    ) : (
                      <>
                        <Database className="mr-2 h-4 w-4" />
                        Generate SQL Scripts
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* SQL Scripts Output */}
              {generatedSqlScripts && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-sm">Generated SQL Scripts</h4>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(generatedSqlScripts)}
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copy SQL
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={downloadSQL}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download SQL
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-96 overflow-auto bg-white rounded border p-4">
                    <pre className="text-xs font-mono whitespace-pre-wrap">
                      {generatedSqlScripts}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Generation Failed</span>
              </div>
              <p className="mt-2 text-sm text-red-600">{typeof error === 'string' ? error : 'An error occurred'}</p>
              {(error.includes("temporarily unavailable") || error.includes("502")) && (
                <div className="mt-3 flex gap-2">
                  <Button 
                    onClick={handleGenerateModule}
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Try Again
                  </Button>
                  <Button 
                    onClick={() => setError(null)}
                    size="sm"
                    variant="ghost"
                    className="text-red-600"
                  >
                    Dismiss
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Add Custom Dependent Target Modal */}
      <Dialog open={showAddDependentModal} onOpenChange={setShowAddDependentModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Add Custom Dependent Target</DialogTitle>
            <DialogDescription>
              Enter the name of the dependent target. AI will generate the configuration based on existing dependent targets.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="newDependentName">Dependent Target Name *</Label>
              <Input
                id="newDependentName"
                value={newDependentName}
                onChange={(e) => setNewDependentName(e.target.value)}
                placeholder="e.g., Tasks, Notes, Approvals"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddCustomDependent();
                  }
                }}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddDependentModal(false);
                  setNewDependentName("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddCustomDependent}
                className="bg-green-600 hover:bg-green-700"
              >
                Add Dependent Target
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Dependent Target Modal for New Target */}
      <Dialog open={showAddDependentTargetModal} onOpenChange={setShowAddDependentTargetModal}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Add Dependent Target</DialogTitle>
            <DialogDescription>
              Define a dependent target that will be created as part of this module.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="dependentTargetName">Target Name *</Label>
              <Input
                id="dependentTargetName"
                value={newTargetData.name}
                onChange={(e) => setNewTargetData(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Conversations, Tasks, Notes"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dependentSourceKey">Source Key for Child *</Label>
              <Input
                id="dependentSourceKey"
                value={newTargetData.sourceKeyForChild}
                onChange={(e) => setNewTargetData(prev => ({...prev, sourceKeyForChild: e.target.value}))}
                placeholder="e.g., conversations, tasks, notes"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dependentUniqueId">Target Unique Identifier *</Label>
              <Input
                id="dependentUniqueId"
                value={newTargetData.targetUniqueIdentifier}
                onChange={(e) => setNewTargetData(prev => ({...prev, targetUniqueIdentifier: e.target.value}))}
                placeholder="e.g., {{conversations.id}}, {{tasks.task_id}}"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddDependentTargetModal(false);
                  setNewTargetData({name: "", sourceKeyForChild: "", targetUniqueIdentifier: "", type: "dependent"});
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (newTargetData.name && newTargetData.sourceKeyForChild && newTargetData.targetUniqueIdentifier) {
                    setCustomDependentTargets(prev => [...prev, {
                      name: newTargetData.name,
                      sourceKeyForChild: newTargetData.sourceKeyForChild,
                      targetUniqueIdentifier: newTargetData.targetUniqueIdentifier
                    }]);
                    setShowAddDependentTargetModal(false);
                    setNewTargetData({name: "", sourceKeyForChild: "", targetUniqueIdentifier: "", type: "dependent"});
                    toast.success("Dependent target added successfully!");
                  } else {
                    toast.error("Please fill in all required fields");
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Add Dependent Target
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Mapping Target Modal for New Target */}
      <Dialog open={showAddMappingTargetModal} onOpenChange={setShowAddMappingTargetModal}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Add Mapping Target</DialogTitle>
            <DialogDescription>
              Define a mapping target that will help with field mappings and transformations.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="mappingTargetName">Target Name *</Label>
              <Input
                id="mappingTargetName"
                value={newTargetData.name}
                onChange={(e) => setNewTargetData(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Users, Companies, Categories"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mappingSourceKey">Source Key for Child</Label>
              <Input
                id="mappingSourceKey"
                value={newTargetData.sourceKeyForChild}
                onChange={(e) => setNewTargetData(prev => ({...prev, sourceKeyForChild: e.target.value}))}
                placeholder="e.g., users, companies (optional)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mappingUniqueId">Target Unique Identifier *</Label>
              <Input
                id="mappingUniqueId"
                value={newTargetData.targetUniqueIdentifier}
                onChange={(e) => setNewTargetData(prev => ({...prev, targetUniqueIdentifier: e.target.value}))}
                placeholder="e.g., {{users.id}}, {{companies.company_id}}"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddMappingTargetModal(false);
                  setNewTargetData({name: "", sourceKeyForChild: "", targetUniqueIdentifier: "", type: "mapping"});
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (newTargetData.name && newTargetData.targetUniqueIdentifier) {
                    setCustomMappingTargets(prev => [...prev, {
                      name: newTargetData.name,
                      sourceKeyForChild: newTargetData.sourceKeyForChild || "",
                      targetUniqueIdentifier: newTargetData.targetUniqueIdentifier
                    }]);
                    setShowAddMappingTargetModal(false);
                    setNewTargetData({name: "", sourceKeyForChild: "", targetUniqueIdentifier: "", type: "mapping"});
                    toast.success("Mapping target added successfully!");
                  } else {
                    toast.error("Please fill in Target Name and Unique Identifier");
                  }
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Add Mapping Target
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* JSON Editor Modal */}
      <Dialog open={jsonEditorModal.open} onOpenChange={(open) => !open && setJsonEditorModal(prev => ({ ...prev, open: false }))}>
        <DialogContent className="max-w-5xl max-h-[90vh] bg-white">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <Edit3 className="h-5 w-5 text-blue-600" />
              Edit JSON: {jsonEditorModal.title}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Edit the JSON configuration using the code editor. Make sure the JSON is valid before saving.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col space-y-4 py-4">
            {/* View Toggle */}
            <div className="flex items-center gap-2 pb-3">
              <Button
                size="sm"
                variant={jsonEditorView === "tree" ? "default" : "outline"}
                onClick={() => setJsonEditorView("tree")}
                className={jsonEditorView === "tree" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
              >
                Tree View
              </Button>
              <Button
                size="sm"
                variant={jsonEditorView === "code" ? "default" : "outline"}
                onClick={() => setJsonEditorView("code")}
                className={jsonEditorView === "code" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
              >
                Code View
              </Button>
            </div>

            {/* Editor Content */}
            <div className="h-[50vh] overflow-hidden">
              {jsonEditorView === "tree" ? (
                <div className="h-full border border-gray-300 rounded-lg p-4 bg-white overflow-auto">
                  {(() => {
                    try {
                      const data = JSON.parse(tempJsonValue);
                      return (
                        <div className="text-sm">
                          {Object.entries(data).map(([key, value]) => (
                            <TreeNode
                              key={key}
                              keyName={key}
                              value={value}
                              path={[key]}
                              onUpdate={updateTreeData}
                              onDelete={deleteTreeNode}
                            />
                          ))}
                        </div>
                      );
                    } catch (err) {
                      return (
                        <div className="text-red-600 p-4 bg-red-50 rounded">
                          <p className="font-medium mb-2">Invalid JSON</p>
                          <p className="text-sm">Please fix the JSON in Code View first.</p>
                          <p className="text-xs mt-2 text-gray-600">{err.message}</p>
                        </div>
                      );
                    }
                  })()}
                </div>
              ) : (
                <div className="h-full">
                  <textarea
                    value={tempJsonValue}
                    onChange={(e) => handleJsonTextChange(e.target.value)}
                    className="w-full h-full font-mono text-sm p-4 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Enter valid JSON..."
                    style={{ fontFamily: 'Monaco, Consolas, "Courier New", monospace', lineHeight: '1.5' }}
                  />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-xs text-gray-600">
                💡 Make sure your JSON is valid before saving
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setJsonEditorModal({ open: false, title: "", value: null, table: null, field: null })}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveJsonEdit}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AIModuleCreator;